/**
 * 피처 플래그 기반 조건부 렌더링 컴포넌트
 * 
 * 특정 피처 플래그가 활성화되었을 때만 자식 컴포넌트를 렌더링합니다.
 */

import { FeatureFlagName } from "constants/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { ReactNode } from "react";

interface FeatureEnabledProps {
    /** 확인할 피처 플래그 이름 */
    featureFlag: FeatureFlagName;
    /** 피처가 활성화되었을 때 렌더링할 컴포넌트 */
    children: ReactNode;
    /** 피처가 비활성화되었을 때 렌더링할 컴포넌트 (선택사항) */
    fallback?: ReactNode;
}

/**
 * 피처 플래그 기반 조건부 렌더링 컴포넌트
 * 
 * @example
 * ```tsx
 * // 기본 사용
 * <FeatureEnabled featureFlag="CHRISTMAS_EVENT">
 *   <ChristmasUI />
 * </FeatureEnabled>
 * 
 * // fallback 사용
 * <FeatureEnabled 
 *   featureFlag="SHOW_VOTING_CARD"
 *   fallback={<NoVotingCard />}
 * >
 *   <VotingCard />
 * </FeatureEnabled>
 * ```
 */
export function FeatureEnabled({
    featureFlag,
    children,
    fallback = null
}: FeatureEnabledProps) {
    const { isFeatureEnabled } = useFeatureFlags();

    return isFeatureEnabled(featureFlag) ? <>{children}</> : <>{fallback}</>;
}
